import { ProductSelectEvent } from '@shopify/events';

/**
 * PDP Buybox — option pickers, subscribe/OTP, and frequency controls.
 * Compatible with Seal Subscriptions via native `selling_plan` + dataset handoff.
 */
class PdpBuybox extends HTMLElement {
  /** @type {MutationObserver | null} */
  #sealObserver = null;

  connectedCallback() {
    this.variantInput = this.querySelector('[data-buybox-variant-id]');
    this.sellingPlanInput = this.querySelector('[data-buybox-selling-plan]');
    this.subscribePanel = this.querySelector('[data-buybox-subscribe-panel]');
    this.deliverHeading = this.querySelector('[data-buybox-deliver-heading]');
    this.deliverSubtext = this.querySelector('[data-buybox-deliver-subtext]');
    this.subscribePriceEl = this.querySelector('[data-buybox-subscribe-price]');
    this.subscribeSaveEl = this.querySelector('[data-buybox-subscribe-save]');
    this.otpPriceEl = this.querySelector('[data-buybox-otp-price]');
    this.frequencyGroup = this.querySelector('[data-buybox-frequency]');

    try {
      this.productData = JSON.parse(this.querySelector('[data-buybox-json]')?.textContent || '{}');
    } catch {
      this.productData = { variants: [], sellingPlans: [], options: [] };
    }

    this.purchaseType = this.dataset.defaultPurchase || 'subscribe';
    this.sellingPlanId = this.dataset.defaultPlan || String(this.productData.sellingPlans?.[0]?.id || '');
    this.variantId = String(this.variantInput?.value || this.productData.variants?.[0]?.id || '');
    this.selectedOptions = this.#optionsFromVariant(this.#currentVariant());
    this.layout = this.dataset.layout || 'subscription';
    this.isSample = this.layout === 'sample';
    this.isTrial = this.layout === 'trial';
    this.isOtpOnly = this.isSample || this.isTrial;

    if (this.isOtpOnly) {
      this.purchaseType = 'otp';
      this.sellingPlanId = '';
    }

    this.addEventListener('click', this.#onClick, true);
    // Re-assert plan right before the theme's add-to-cart submit reads the form.
    this.addEventListener('pointerdown', this.#onPointerDown, true);
    this.addEventListener('submit', this.#onSubmit, true);
    this.quantityInput = this.querySelector('[data-buybox-quantity]');
    this.quantityDisplay = this.querySelector('[data-buybox-quantity-display]');
    this.quantityMin = 1;
    this.quantity = Math.max(this.quantityMin, Number(this.quantityInput?.value) || this.quantityMin);

    this.#renderFrequencyButtons();
    this.#stripSealWidget();
    this.#observeSealWidget();
    this.#preloadVariantMedia();
    this.#sync();
    this.#syncQuantity();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick, true);
    this.removeEventListener('pointerdown', this.#onPointerDown, true);
    this.removeEventListener('submit', this.#onSubmit, true);
    this.#sealObserver?.disconnect();
    this.#sealObserver = null;
  }

  #onPointerDown = (event) => {
    const submitter = event.target.closest('button[type="submit"], add-to-cart-component');
    if (submitter && this.contains(submitter)) {
      this.#assertSellingPlan();
    }
  };

  #onSubmit = () => {
    this.#assertSellingPlan();
  };

  #onClick = (event) => {
    const target = event.target.closest('[data-buybox-action]');
    if (!target || !this.contains(target)) return;

    const action = target.dataset.buyboxAction;
    const value = target.dataset.buyboxValue;

    if (action === 'scroll-reviews') {
      event.preventDefault();
      event.stopPropagation();
      this.#scrollToReviews();
      return;
    }

    if (action === 'option') {
      const position = Number(target.dataset.buyboxOptionPosition || 0);
      if (position >= 1 && position <= 3) {
        const previousVariantId = this.variantId;
        this.selectedOptions[position - 1] = value;
        const matched = this.#findVariant(this.selectedOptions);
        if (matched) {
          this.variantId = String(matched.id);
          this.selectedOptions = this.#optionsFromVariant(matched);
        }
        this.#sync();
        this.#updateUrl();
        if (this.variantId !== previousVariantId) {
          this.#notifyVariantChange();
        }
      }
      return;
    }

    // Legacy pack buttons (variant id)
    if (action === 'pack') {
      const previousVariantId = this.variantId;
      this.variantId = String(value);
      this.selectedOptions = this.#optionsFromVariant(this.#currentVariant());
      this.#sync();
      this.#updateUrl();
      if (this.variantId !== previousVariantId) {
        this.#notifyVariantChange();
      }
      return;
    }

    if (action === 'purchase') {
      if (this.isOtpOnly) return;
      this.purchaseType = value;
      this.#sync();
      return;
    }

    if (action === 'frequency') {
      if (this.isOtpOnly) return;
      this.sellingPlanId = String(value);
      this.purchaseType = 'subscribe';
      this.#sync();
    }

    if (action === 'quantity') {
      const current = Number(this.quantityInput?.value) || this.quantityMin;
      const next = value === 'increase'
        ? current + 1
        : Math.max(this.quantityMin, current - 1);

      this.quantity = next;
      this.#syncQuantity();
      return;
    }
  };

  #scrollToReviews() {
    const target =
      document.querySelector('#judgeme_product_reviews') ||
      document.querySelector('[id*="judge_me_reviews_review_widget"]') ||
      document.querySelector('.jdgm-review-widget') ||
      document.querySelector('.jdgm-rev-widg');

    if (!target) return;

    const headerOffset =
      Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) ||
      Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) ||
      0;
    const offset = headerOffset + 16;

    // This theme scrolls `.page-wrapper` on desktop, not `window`.
    const scroller = this.#getScrollParent(target);
    const top =
      scroller === document.scrollingElement || scroller === document.documentElement || scroller === document.body
        ? target.getBoundingClientRect().top + window.scrollY - offset
        : target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - offset;

    scroller.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }

  /**
   * @param {Element} el
   * @returns {Element}
   */
  #getScrollParent(el) {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) {
      const { overflowY } = getComputedStyle(pageWrapper);
      if (overflowY === 'auto' || overflowY === 'scroll' || pageWrapper.scrollHeight > pageWrapper.clientHeight) {
        return pageWrapper;
      }
    }

    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      const { overflowY } = getComputedStyle(parent);
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return document.scrollingElement || document.documentElement;
  }

  /**
   * Seal auto-injects `.sealsubs-container` into product forms.
   * Keep our custom UI and a single authoritative selling_plan input.
   */
  #stripSealWidget() {
    const form = this.querySelector('form[data-type="add-to-cart-form"], form.shopify-product-form');
    if (!form) return;

    form.querySelectorAll(
      '.sealsubs-container, .sealsubs-target-element, #sealsubscriptions-default-widget-target-element'
    ).forEach((el) => {
      if (el.hasAttribute('data-buybox-keep')) return;
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
      el.style.setProperty('display', 'none', 'important');
      el.querySelectorAll('input, select, textarea, button').forEach((field) => {
        field.setAttribute('disabled', 'disabled');
        if (field.getAttribute('name') === 'selling_plan') {
          field.removeAttribute('name');
        }
      });
    });

    form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
      if (!input.hasAttribute('data-buybox-selling-plan')) {
        input.removeAttribute('name');
        input.setAttribute('disabled', 'disabled');
      }
    });
  }

  #observeSealWidget() {
    const form = this.querySelector('form[data-type="add-to-cart-form"], form.shopify-product-form');
    if (!form || this.#sealObserver) return;

    this.#sealObserver = new MutationObserver(() => {
      this.#stripSealWidget();
      this.#assertSellingPlan();
    });
    this.#sealObserver.observe(form, { childList: true, subtree: true });
  }

  #optionsFromVariant(variant) {
    if (!variant) return [null, null, null];
    return [variant.option1 || null, variant.option2 || null, variant.option3 || null];
  }

  #findVariant(selectedOptions) {
    const variants = this.productData.variants || [];
    const exact = variants.find((variant) => {
      return [1, 2, 3].every((position) => {
        const selected = selectedOptions[position - 1];
        if (selected == null || selected === '') return true;
        return String(variant[`option${position}`] || '') === String(selected);
      });
    });
    if (exact) return exact;

    // Prefer an available partial match when the exact combo does not exist.
    return (
      variants.find((variant) => {
        return selectedOptions.every((selected, index) => {
          if (selected == null || selected === '') return true;
          return String(variant[`option${index + 1}`] || '') === String(selected);
        });
      }) || variants[0]
    );
  }

  #currentVariant() {
    return (
      (this.productData.variants || []).find((v) => String(v.id) === String(this.variantId)) ||
      this.productData.variants?.[0]
    );
  }

  #currentPlan() {
    return (
      (this.productData.sellingPlans || []).find((p) => String(p.id) === String(this.sellingPlanId)) ||
      this.productData.sellingPlans?.[0] ||
      null
    );
  }

  #currentAllocation() {
    const variant = this.#currentVariant();
    const plan = this.#currentPlan();
    if (!variant || !plan) return null;
    return (
      (variant.sellingPlanAllocations || []).find((a) => String(a.sellingPlanId) === String(plan.id)) || null
    );
  }

  #formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.productData.currency || 'USD',
        minimumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  #savePercent(otpCents, subCents) {
    if (!otpCents || subCents == null) return 0;
    return Math.round(((otpCents - subCents) / otpCents) * 100);
  }

  #gummiesCount(variant) {
    const sizeOption = (this.productData.options || []).find((option) => {
      const name = String(option.name || '').toLowerCase();
      return name === 'size' || name.includes('size') || name.includes('quantity') || name.includes('pack');
    });
    const sizeValue = sizeOption ? variant?.[`option${sizeOption.position}`] : variant?.option1;
    const title = sizeValue || variant?.title || '';
    const match = String(title).match(/(\d+)\s*Gummies/i);
    return match ? Number(match[1]) : null;
  }

  #isOptionValueAvailable(position, value) {
    const selected = [...this.selectedOptions];
    selected[position - 1] = value;
    const match = (this.productData.variants || []).find((variant) => {
      return [1, 2, 3].every((pos) => {
        const selectedValue = selected[pos - 1];
        if (selectedValue == null || selectedValue === '') return true;
        return String(variant[`option${pos}`] || '') === String(selectedValue);
      });
    });
    return Boolean(match?.available);
  }

  /** Parse Seal option values like "month", "2 months", "30 days", "week". */
  #parseDeliveryInterval(optionValue) {
    const raw = String(optionValue || '').trim().toLowerCase();
    if (!raw) return { label: 'Deliver on schedule', days: null, unitLabel: '' };

    const weekMatch = raw.match(/(\d+)?\s*weeks?/);
    if (weekMatch) {
      const n = Number(weekMatch[1] || 1);
      return {
        label: n === 1 ? 'Deliver every week' : `Deliver every ${n} weeks`,
        days: n * 7,
        unitLabel: n === 1 ? 'every 7 days' : `every ${n * 7} days`,
      };
    }

    const dayMatch = raw.match(/(\d+)?\s*days?/);
    if (dayMatch) {
      const n = Number(dayMatch[1] || 1);
      return {
        label: n === 1 ? 'Deliver every day' : `Deliver every ${n} days`,
        days: n,
        unitLabel: n === 1 ? 'every day' : `every ${n} days`,
      };
    }

    const monthMatch = raw.match(/(\d+)?\s*months?/) || (raw === 'month' ? [null, '1'] : null);
    if (monthMatch) {
      const n = Number(monthMatch[1] || 1);
      return {
        label: n === 1 ? 'Deliver every month' : `Deliver every ${n} months`,
        days: n * 30,
        unitLabel: n === 1 ? 'every 30 days' : `every ${n * 30} days`,
      };
    }

    return {
      label: `Deliver every ${optionValue}`,
      days: null,
      unitLabel: `every ${optionValue}`,
    };
  }

  #formatPlanButtonLabel(plan) {
    const value = String(plan?.optionValue || '').trim().toLowerCase();
    if (value === 'month' || value === '1 month') return 'Monthly Subscription';
    if (/^2\s*months?$/.test(value) || value.includes('2 month')) return '2 Months Subscription';
    if (/^\d+\s*months?$/.test(value)) {
      const n = value.match(/^(\d+)/)?.[1];
      return `${n} Months Subscription`;
    }
    // Fall back to Seal plan name
    return plan?.name || 'Subscription';
  }

#renderFrequencyButtons() {
  if (!this.frequencyGroup) return;
  const plans = this.productData.sellingPlans || [];
  if (!plans.length) {
    this.frequencyGroup.hidden = true;
    return;
  }

  this.frequencyGroup.hidden = false;

  const existingButtons = [...this.frequencyGroup.querySelectorAll('[data-buybox-action="frequency"]')];
  const isInSync =
    existingButtons.length === plans.length &&
    existingButtons.every((btn, i) => String(btn.dataset.buyboxValue) === String(plans[i].id));

  // Only rebuild markup the first time (or if the plan list itself ever changes).
  // Rebuilding on every sync() destroys whichever button the user just clicked
  // mid-event-dispatch, which makes an ancestor "click outside" listener (e.g. the
  // quick-add dialog's) think the click landed outside the dialog and close it.
  if (!isInSync) {
    this.frequencyGroup.innerHTML = plans
      .map((plan) => {
        const label = this.#formatPlanButtonLabel(plan);
        return `<button
          type="button"
          class="pdp-buybox__freq-btn"
          data-buybox-action="frequency"
          data-buybox-value="${plan.id}"
          aria-pressed="false"
        >${label}</button>`;
      })
      .join('');
  }

  // Always just toggle state on the (now-stable) existing buttons.
  this.frequencyGroup.querySelectorAll('[data-buybox-action="frequency"]').forEach((btn) => {
    const active = String(btn.dataset.buyboxValue) === String(this.sellingPlanId || plans[0].id);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
}

  #assertSellingPlan() {
    if (!this.sellingPlanInput) return;

    // Always keep our field as the only named selling_plan input.
    this.sellingPlanInput.setAttribute('name', 'selling_plan');
    this.sellingPlanInput.removeAttribute('disabled');
    this.sellingPlanInput.setAttribute('data-buybox-selling-plan', '');

    if (this.isOtpOnly || this.purchaseType !== 'subscribe') {
      this.sellingPlanInput.value = '';
      this.dataset.sellingPlanId = '';
      this.dataset.purchaseType = 'otp';
      this.purchaseType = 'otp';
      return;
    }

    const plan = this.#currentPlan();
    const planId = plan ? String(plan.id) : '';
    this.sellingPlanInput.value = planId;
    this.dataset.sellingPlanId = planId;
    this.dataset.purchaseType = 'subscribe';
  }

  #sync() {
    const variant = this.#currentVariant();
    if (!variant) return;

    this.selectedOptions = this.#optionsFromVariant(variant);

    if (this.isOtpOnly) {
      this.purchaseType = 'otp';
      this.sellingPlanId = '';
    } else if (!this.sellingPlanId && this.productData.sellingPlans?.length) {
      this.sellingPlanId = String(this.productData.sellingPlans[0].id);
    }

    const plan = this.#currentPlan();
    const allocation = this.#currentAllocation();
    const otpCents = variant.price;
    const compareCents = variant.compareAtPrice;
    const subCents = allocation?.price ?? Math.round(otpCents * 0.7);
    const savePct = allocation
      ? this.#savePercent(otpCents, subCents)
      : Number(this.dataset.fallbackSave || 30);

    if (this.variantInput) this.variantInput.value = String(variant.id);

    this.#assertSellingPlan();
    this.#stripSealWidget();
    if (!this.isOtpOnly) this.#renderFrequencyButtons();

    this.querySelectorAll('[data-buybox-action="option"]').forEach((el) => {
      const position = Number(el.dataset.buyboxOptionPosition || 0);
      const value = el.dataset.buyboxValue;
      const active = String(this.selectedOptions[position - 1] || '') === String(value);
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
      const available = this.#isOptionValueAvailable(position, value);
      el.disabled = !available;
    });

    this.querySelectorAll('[data-buybox-action="pack"]').forEach((el) => {
      const active = String(el.dataset.buyboxValue) === String(variant.id);
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.querySelectorAll('[data-buybox-action="purchase"]').forEach((el) => {
      const active = el.dataset.buyboxValue === this.purchaseType;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (this.subscribePanel) {
      this.subscribePanel.hidden = this.isOtpOnly || this.purchaseType !== 'subscribe';
    }

    if (this.subscribePriceEl) this.subscribePriceEl.textContent = this.#formatMoney(subCents);
    if (this.otpPriceEl) this.otpPriceEl.textContent = this.#formatMoney(otpCents);
    const compareEl = this.querySelector('[data-buybox-compare-price]');
    if (compareEl) {
      if (compareCents && compareCents > otpCents) {
        compareEl.hidden = false;
        compareEl.textContent = this.#formatMoney(compareCents);
      } else {
        compareEl.hidden = true;
      }
    }
    if (this.subscribeSaveEl) {
      this.subscribeSaveEl.textContent = savePct > 0 ? `Save ${savePct}%` : '';
      this.subscribeSaveEl.hidden = savePct <= 0;
    }

    if (this.isOtpOnly) return;

    const interval = this.#parseDeliveryInterval(plan?.optionValue);
    const gummies = this.#gummiesCount(variant);

    if (this.deliverHeading) {
      this.deliverHeading.textContent = interval.label;
    }
    if (this.deliverSubtext) {
      if (gummies && interval.unitLabel) {
        this.deliverSubtext.textContent = `${gummies} gummies ${interval.unitLabel}`;
      } else if (interval.unitLabel) {
        this.deliverSubtext.textContent = interval.unitLabel;
      } else {
        this.deliverSubtext.textContent = plan?.name || '';
      }
    }
  }

  #syncQuantity() {
    if (this.quantityInput) this.quantityInput.value = String(this.quantity);
    if (this.quantityDisplay) this.quantityDisplay.textContent = String(this.quantity);

    const minusBtn = this.querySelector(
      '[data-buybox-action="quantity"][data-buybox-value="decrease"]'
    );
    if (minusBtn instanceof HTMLButtonElement) {
      minusBtn.disabled = this.quantity <= this.quantityMin;
    }
  }
  
  #updateUrl() {
    if (!this.variantId || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Preload all variant featured images so pack switches feel instant.
   */
  #preloadVariantMedia() {
    const seen = new Set();
    for (const variant of this.productData.variants || []) {
      const media = variant?.featuredMedia;
      if (!media?.src || seen.has(media.src)) continue;
      seen.add(media.src);
      const img = new Image();
      if (media.srcset) img.srcset = media.srcset;
      img.src = media.src;
      if (media.thumbSrc) {
        const thumb = new Image();
        thumb.src = media.thumbSrc;
      }
    }
  }

  /**
   * Instantly swap the gallery (and ATC fly-to-cart) image for the selected variant.
   * Avoids a section-rendering round-trip so the change is visible immediately.
   */
  #notifyVariantChange() {
    const variant = this.#currentVariant();
    if (!variant) return;

    this.#applyVariantMedia(variant);
    this.#dispatchProductSelect(variant);
  }

  /**
   * @param {object} variant
   */
  #applyVariantMedia(variant) {
    const media = variant?.featuredMedia;
    if (!media?.src) return;

    const section = this.closest('.shopify-section');
    const gallery = section?.querySelector('media-gallery');

    if (gallery) {
      // With hide_variants, the first slide is the variant featured image.
      const firstSlide = gallery.querySelector('slideshow-slide');
      const mainImages = firstSlide
        ? firstSlide.querySelectorAll('.product-media__image, .deferred-media__poster-image')
        : gallery.querySelectorAll('.product-media__image');

      mainImages.forEach((img) => this.#setImageSources(img, media));

      const mediaRoot = firstSlide?.querySelector('[data-media-id]');
      if (mediaRoot && media.id != null) {
        mediaRoot.setAttribute('data-media-id', String(media.id));
      }

      // First thumbnail mirrors the featured variant image.
      const firstThumb = gallery.querySelector(
        '.slideshow-controls__thumbnail img, button.slideshow-controls__thumbnail img'
      );
      if (firstThumb) {
        this.#setImageSources(firstThumb, {
          src: media.thumbSrc || media.src,
          alt: media.alt,
        });
      }

      // Keep zoom dialog first frame in sync when present.
      const zoomFirst = gallery.querySelector(
        'zoom-dialog [ref="media"], zoom-dialog .product-media__image'
      );
      if (zoomFirst instanceof HTMLImageElement) {
        this.#setImageSources(zoomFirst, media);
      } else if (zoomFirst) {
        zoomFirst.querySelectorAll('img').forEach((img) => this.#setImageSources(img, media));
      }

      const slideshow = /** @type {{ select?: Function } | null} */ (
        gallery.querySelector('slideshow-component')
      );
      slideshow?.select?.(0, undefined, { animate: false });
    }

    // Fly-to-cart / sticky ATC preview image
    const previewSrc = media.preview_image?.src || media.thumbSrc || media.src;
    this.querySelectorAll('[data-product-variant-media]').forEach((el) => {
      el.setAttribute('data-product-variant-media', previewSrc);
    });

    const atcButton = this.querySelector('[ref="addToCartButton"]');
    if (atcButton instanceof HTMLButtonElement) {
      atcButton.disabled = !variant.available;
    }
  }

  /**
   * @param {Element} img
   * @param {{ src?: string, srcset?: string, alt?: string }} media
   */
  #setImageSources(img, media) {
    if (!(img instanceof HTMLImageElement) || !media?.src) return;
    if (media.srcset) img.srcset = media.srcset;
    else img.removeAttribute('srcset');
    img.src = media.src;
    if (media.alt != null) img.alt = media.alt;
    if (img.hasAttribute('data-max-resolution')) {
      img.setAttribute('data-max-resolution', media.src);
    }
  }

  /**
   * Keep theme listeners in sync without waiting on a section fetch.
   * @param {object} variant
   */
  #dispatchProductSelect(variant) {
    const selectedOptions = this.#getSelectedOptionsPayload(variant);
    if (!selectedOptions.length) return;

    const deferred = ProductSelectEvent.createPromise();
    const featuredMedia = variant.featuredMedia || null;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {},
        promise: deferred.promise,
      })
    );

    // Resolve immediately — gallery already updated client-side.
    deferred.resolve({
      variant: {
        id: String(variant.id),
        title: variant.title || '',
        availableForSale: Boolean(variant.available),
        price: {
          amount: String((Number(variant.price) || 0) / 100),
          currencyCode: this.productData.currency || 'USD',
        },
        selectedOptions,
      },
      detail: {
        productId: this.dataset.productId ?? '',
        sourceId: String(variant.id),
        resource: {
          id: String(variant.id),
          title: variant.title,
          available: variant.available,
          price: variant.price,
          featured_media: featuredMedia
            ? {
                preview_image: {
                  src: featuredMedia.preview_image?.src || featuredMedia.src,
                },
              }
            : null,
        },
      },
    });
  }

  /**
   * @param {object} variant
   * @returns {{ name: string, value: string }[]}
   */
  #getSelectedOptionsPayload(variant) {
    const options = this.productData.options || [];
    /** @type {{ name: string, value: string }[]} */
    const selected = [];

    for (const option of options) {
      const value = variant?.[`option${option.position}`];
      if (value == null || value === '') continue;
      selected.push({ name: option.name || `Option ${option.position}`, value: String(value) });
    }

    if (selected.length) return selected;

    return [1, 2, 3]
      .map((position) => {
        const value = variant?.[`option${position}`];
        if (value == null || value === '') return null;
        return { name: `Option ${position}`, value: String(value) };
      })
      .filter(Boolean);
  }
}

if (!customElements.get('pdp-buybox')) {
  customElements.define('pdp-buybox', PdpBuybox);
}
